import { customerBrowserHeaders, writeCustomerSession } from './customerSession';

export type CustomerSocialProvider = 'google' | 'apple';
export type CustomerSocialEnvironment = 'browser' | 'embedded' | 'native';
export type CustomerSocialStatusState = 'idle' | 'opening' | 'verifying' | 'success' | 'error' | 'external-required';
export type CustomerSocialStatus = {
  provider: CustomerSocialProvider;
  state: CustomerSocialStatusState;
  message: string;
};

type ProviderReadiness = { google?: { ready?: boolean }; apple?: { ready?: boolean } };
type GoogleCredentialResponse = { credential?: string };
type GoogleIdentity = {
  initialize(input: Record<string, unknown>): void;
  renderButton(host: HTMLElement, input: Record<string, unknown>): void;
  prompt(callback?: (notification: unknown) => void): void;
};
type AppleResponse = { authorization?: { id_token?: string } };
type AppleAuth = {
  init(input: { clientId: string; scope: string; redirectURI: string; state: string; nonce: string; usePopup: boolean }): void;
  signIn(): Promise<AppleResponse>;
};
type NativeTokenEvent = CustomEvent<{ provider?: CustomerSocialProvider; providerToken?: string }>;

declare global {
  interface Window {
    AppleID?: { auth?: AppleAuth };
    google?: { accounts?: { id?: GoogleIdentity } };
    TimSocialAuth?: { postMessage(provider: CustomerSocialProvider): void };
  }
}

const GOOGLE_CLIENT_ID = '568557392958-4olv9n57t9lnvbapmmtdei4dr5juujqo.apps.googleusercontent.com';
const APPLE_CLIENT_ID = 'vn.timmassage.web';
const GOOGLE_SCRIPT_ID = 'tim-customer-google-identity';
const APPLE_SCRIPT_ID = 'tim-customer-apple-identity';
const EMBEDDED_UA = /(FBAN|FBAV|Instagram|Line\/|Zalo|TikTok|Twitter|Snapchat|MicroMessenger|;\s*wv\)|\bwv\b|WebView)/i;
const IOS_DEVICE = /iP(?:hone|ad|od)/i;

function randomValue() {
  return Array.from(crypto.getRandomValues(new Uint8Array(24)), (value) => value.toString(16).padStart(2, '0')).join('');
}

function customerDeviceId() {
  const key = 'tim-customer-device-id';
  let value = localStorage.getItem(key);
  if (!value) {
    value = `customer-web-${crypto.randomUUID()}`;
    localStorage.setItem(key, value);
  }
  return value;
}

function safeDestination() {
  const candidate = new URLSearchParams(location.search).get('redirect_to');
  if (!candidate) return `${location.origin}/ca-nhan/`;
  try {
    const target = new URL(candidate, location.origin);
    return target.origin === location.origin ? target.href : `${location.origin}/ca-nhan/`;
  } catch {
    return `${location.origin}/ca-nhan/`;
  }
}

function loadScript(id: string, src: string, ready: () => boolean) {
  return new Promise<void>((resolve, reject) => {
    if (ready()) { resolve(); return; }
    const existing = document.getElementById(id) as HTMLScriptElement | null;
    const script = existing || document.createElement('script');
    const loaded = () => ready() ? resolve() : reject(new Error('Dịch vụ đăng nhập tải xong nhưng chưa sẵn sàng.'));
    script.addEventListener('load', loaded, { once: true });
    script.addEventListener('error', () => reject(new Error('Không tải được dịch vụ đăng nhập.')), { once: true });
    if (existing) return;
    script.id = id;
    script.src = src;
    script.async = true;
    script.defer = true;
    document.head.append(script);
  });
}

export function detectCustomerSocialEnvironment(userAgent = navigator.userAgent): CustomerSocialEnvironment {
  if (typeof window.TimSocialAuth?.postMessage === 'function') return 'native';
  const iosWebView = IOS_DEVICE.test(userAgent) && /AppleWebKit/i.test(userAgent) && !/Safari/i.test(userAgent);
  return EMBEDDED_UA.test(userAgent) || iosWebView ? 'embedded' : 'browser';
}

async function exchangeProvider(provider: CustomerSocialProvider, providerToken: string) {
  const response = await fetch('/api/v1/auth/provider', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...customerBrowserHeaders },
    body: JSON.stringify({
      provider,
      providerToken,
      tenantId: 'massage',
      audience: 'tim-massage',
      deviceId: customerDeviceId(),
    }),
  });
  const envelope = await response.json().catch(() => null);
  if (!response.ok || !envelope?.data?.accessToken) {
    throw new Error(envelope?.error?.message || `Không thể đăng nhập bằng ${provider === 'google' ? 'Google' : 'Apple'}.`);
  }
  writeCustomerSession(envelope.data);
  return envelope.data;
}

export function createCustomerSocialAuth(options: {
  onStatus?: (status: CustomerSocialStatus) => void;
  onAuthenticated?: () => void;
  externalHelpDelayMs?: number;
} = {}) {
  const environment = detectCustomerSocialEnvironment();
  const readiness: Record<CustomerSocialProvider, boolean> = { google: false, apple: false };
  const timers = new Set<number>();
  let disposed = false;
  let googleMounted = false;

  const report = (provider: CustomerSocialProvider, state: CustomerSocialStatusState, message: string) => {
    if (!disposed) options.onStatus?.({ provider, state, message });
  };
  const clearTimers = () => {
    for (const timer of timers) window.clearTimeout(timer);
    timers.clear();
  };
  const scheduleExternalHelp = (provider: CustomerSocialProvider) => {
    const timer = window.setTimeout(() => {
      timers.delete(timer);
      report(provider, 'external-required', 'Nếu cửa sổ đăng nhập không quay lại, hãy mở trang này trực tiếp bằng Chrome hoặc Safari.');
    }, options.externalHelpDelayMs ?? 10_000);
    timers.add(timer);
  };
  const authenticated = () => {
    clearTimers();
    options.onAuthenticated?.();
    if (!options.onAuthenticated) location.assign(safeDestination());
  };
  const acceptToken = async (provider: CustomerSocialProvider, token: string) => {
    clearTimers();
    report(provider, 'verifying', `Đang xác minh tài khoản ${provider === 'google' ? 'Google' : 'Apple'}…`);
    try {
      await exchangeProvider(provider, token);
      report(provider, 'success', 'Đăng nhập thành công. Đang chuyển trang…');
      authenticated();
    } catch (error) {
      report(provider, 'error', error instanceof Error ? error.message : 'Không thể hoàn tất đăng nhập.');
    }
  };
  const nativeToken = (event: Event) => {
    const detail = (event as NativeTokenEvent).detail;
    if (detail?.provider && detail.providerToken) void acceptToken(detail.provider, detail.providerToken);
  };
  window.addEventListener('tm-native-social-token', nativeToken);

  return {
    environment,
    configure(providers: ProviderReadiness) {
      readiness.google = providers?.google?.ready === true;
      readiness.apple = providers?.apple?.ready === true;
    },
    ready(provider: CustomerSocialProvider) { return readiness[provider]; },
    async mountGoogle(host: HTMLElement) {
      if (googleMounted || !readiness.google || environment !== 'browser') return false;
      try {
        await loadScript(GOOGLE_SCRIPT_ID, 'https://accounts.google.com/gsi/client', () => Boolean(window.google?.accounts?.id));
        const google = window.google?.accounts?.id;
        if (!google) throw new Error('Google chưa sẵn sàng.');
        google.initialize({
          client_id: GOOGLE_CLIENT_ID,
          ux_mode: 'popup',
          auto_select: false,
          itp_support: true,
          use_fedcm_for_prompt: false,
          use_fedcm_for_button: false,
          button_auto_select: false,
          callback: (result: GoogleCredentialResponse) => result?.credential
            ? void acceptToken('google', result.credential)
            : report('google', 'error', 'Google không trả về thông tin đăng nhập.'),
        });
        google.renderButton(host, {
          type: 'standard', theme: 'outline', size: 'large', width: 190,
          text: 'continue_with', click_listener: () => {
            report('google', 'opening', 'Đang mở Google…');
            scheduleExternalHelp('google');
          },
        });
        // The official popup reuses accounts already signed in to Chrome without
        // asking for the email/password again. Do not start One Tap in the
        // background: current Chrome versions can route it back through FedCM
        // and leave a rejected request even when the popup flow works.
        googleMounted = true;
        return true;
      } catch (error) {
        report('google', 'error', error instanceof Error ? error.message : 'Không thể khởi tạo Google.');
        return false;
      }
    },
    async start(provider: CustomerSocialProvider) {
      if (!readiness[provider]) {
        report(provider, 'error', `Đăng nhập ${provider === 'google' ? 'Google' : 'Apple'} chưa được cấu hình trên máy chủ.`);
        return;
      }
      if (environment === 'native') {
        report(provider, 'opening', `Đang mở ${provider === 'google' ? 'Google' : 'Apple'}…`);
        window.TimSocialAuth?.postMessage(provider);
        scheduleExternalHelp(provider);
        return;
      }
      if (environment === 'embedded') {
        report(provider, 'external-required', 'Trình duyệt hiện tại không hỗ trợ đăng nhập an toàn. Hãy mở trang này trực tiếp bằng Chrome hoặc Safari.');
        return;
      }
      if (provider === 'google') {
        report('google', 'error', googleMounted
          ? 'Vui lòng bấm nút Google chính thức để tiếp tục.'
          : 'Google chưa sẵn sàng. Hãy tải lại trang hoặc mở bằng Chrome/Safari.');
        return;
      }
      report('apple', 'opening', 'Đang mở Apple…');
      scheduleExternalHelp('apple');
      try {
        await loadScript(APPLE_SCRIPT_ID, 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js', () => Boolean(window.AppleID?.auth));
        const apple = window.AppleID?.auth;
        if (!apple) throw new Error('Apple chưa sẵn sàng.');
        apple.init({
          clientId: APPLE_CLIENT_ID,
          scope: 'name email',
          redirectURI: `${location.origin}/apple-login/callback/`,
          state: randomValue(),
          nonce: randomValue(),
          usePopup: true,
        });
        const result = await apple.signIn();
        const token = result?.authorization?.id_token;
        if (!token) throw new Error('Apple không trả về thông tin đăng nhập.');
        await acceptToken('apple', token);
      } catch (error) {
        clearTimers();
        const code = String((error as { error?: string; code?: string })?.error || (error as { code?: string })?.code || '');
        if (code === 'user_cancelled_authorize' || code === 'popup_closed_by_user') {
          report('apple', 'idle', 'Bạn đã đóng cửa sổ đăng nhập.');
        } else if (code === 'popup_blocked_by_browser') {
          report('apple', 'idle', 'Trình duyệt đang chặn cửa sổ đăng nhập. Hãy cho phép cửa sổ bật lên rồi thử lại.');
        } else {
          report('apple', 'error', error instanceof Error ? error.message : 'Không thể mở đăng nhập Apple.');
        }
      }
    },
    dispose() {
      disposed = true;
      clearTimers();
      window.removeEventListener('tm-native-social-token', nativeToken);
    },
  };
}

function googleVisual() {
  return '<span class="tm-node-google-visual" aria-hidden="true"><svg viewBox="0 0 24 24"><path fill="#4285F4" d="M21.6 12.23c0-.71-.06-1.4-.18-2.07H12v3.92h5.38a4.6 4.6 0 0 1-2 3.02v2.54h3.24c1.9-1.75 2.98-4.32 2.98-7.41Z"/><path fill="#34A853" d="M12 22c2.7 0 4.97-.9 6.62-2.36l-3.24-2.54c-.9.6-2.05.96-3.38.96-2.6 0-4.81-1.76-5.6-4.13H3.06v2.62A10 10 0 0 0 12 22Z"/><path fill="#FBBC05" d="M6.4 13.93A6.02 6.02 0 0 1 6.09 12c0-.67.12-1.32.31-1.93V7.45H3.06A10 10 0 0 0 2 12c0 1.61.39 3.14 1.06 4.55l3.34-2.62Z"/><path fill="#EA4335" d="M12 5.94c1.47 0 2.79.5 3.83 1.5l2.87-2.88A9.63 9.63 0 0 0 12 2a10 10 0 0 0-8.94 5.45l3.34 2.62C7.19 7.7 9.4 5.94 12 5.94Z"/></svg><span>Google</span></span>';
}

let legacyController: ReturnType<typeof createCustomerSocialAuth> | null = null;

export async function installLegacyCustomerSocialAuth() {
  const socials = document.querySelector<HTMLElement>('.tm-phone-socials');
  if (!socials || socials.dataset.nodeSocialAuth === '1') return;
  socials.dataset.nodeSocialAuth = '1';
  const response = await fetch('/api/v1/auth/providers/status?tenantId=massage', { credentials: 'same-origin', headers: customerBrowserHeaders });
  const envelope = await response.json().catch(() => null);
  legacyController?.dispose();
  legacyController = createCustomerSocialAuth({
    onStatus(status) {
      let node = socials.parentElement?.querySelector<HTMLElement>('[data-node-social-status]') || null;
      if (!node) {
        node = document.createElement('p');
        node.dataset.nodeSocialStatus = '';
        node.className = 'tm-node-social-status';
        node.setAttribute('role', status.state === 'error' ? 'alert' : 'status');
        node.setAttribute('aria-live', 'polite');
        socials.insertAdjacentElement('afterend', node);
      }
      node.dataset.state = status.state;
      node.textContent = status.message;
    },
  });
  legacyController.configure(response.ok ? envelope?.data || {} : {});

  const legacyGoogle = socials.querySelector<HTMLElement>('[data-oauth-provider="google"]');
  if (legacyGoogle) {
    const host = document.createElement('div');
    host.className = 'tm-node-social-login tm-node-google-login';
    host.setAttribute('aria-label', 'Đăng nhập bằng Google');
    legacyGoogle.replaceWith(host);
    host.insertAdjacentHTML('beforeend', googleVisual());
    const hitTarget = document.createElement('span');
    hitTarget.className = 'tm-node-google-hit-target';
    host.append(hitTarget);
    const mounted = await legacyController.mountGoogle(hitTarget);
    if (!mounted) {
      host.setAttribute('role', 'button');
      host.tabIndex = 0;
      const start = () => void legacyController?.start('google');
      host.addEventListener('click', start);
      host.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); start(); }
      });
    }
  }

  if (!socials.querySelector('[data-node-apple-login]')) {
    const apple = document.createElement('button');
    apple.type = 'button';
    apple.className = 'tm-node-social-login tm-node-apple-login';
    apple.dataset.nodeAppleLogin = '1';
    apple.setAttribute('aria-label', 'Đăng nhập bằng Apple');
    apple.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.77 3.08.83 1.18-.24 2.31-.96 3.57-.87 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 3-2.54 4.09l.01-.01zM12.03 7.25C11.88 5.02 13.69 3.18 15.77 3c.29 2.57-2.33 4.49-3.74 4.25z"/></svg><span>Apple</span>';
    apple.disabled = !legacyController.ready('apple');
    apple.addEventListener('click', () => void legacyController?.start('apple'));
    socials.append(apple);
  }
}
